import React, { useState } from "react";
import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";

import {
  clearSessionLogBuffer,
  downloadFilteredSessionLogBuffer,
  downloadSessionLogBuffer,
  filterSessionLogsByMessage,
  isLocalhost,
  parseSessionLogBuffer,
  type SessionLogRecord,
} from "./devLogDownloadUtils";

import {
  VNextButton,
  VNextCard,
  VNextDialog,
  VNextField,
  VNextInput,
  VNextNotice,
} from "@/vnext/primitives";
import "@/vnext/theme/vnext.css";

export {
  filterSessionLogsByMessage,
  parseSessionLogBuffer,
  type SessionLogRecord,
};

const logger = FrontendLoggerFactory.getLogger().withComponent(
  "DevLogDownloadButton",
);

export const DevLogDownloadButton: React.FC = () => {
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isFilterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filterTermInput, setFilterTermInput] = useState("");

  if (!isLocalhost()) {
    return null;
  }

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const closeFilterDialog = () => {
    setFilterDialogOpen(false);
    setFilterTermInput("");
  };

  const handleDownload = async () => {
    try {
      const result = await downloadSessionLogBuffer();
      if (result.status === "missing") {
        logger.warn("No logs found in local storage session buffer.");
        return;
      }

      if (result.status === "empty") {
        logger.warn("Session log buffer is empty.");
      }
    } catch (error) {
      logger.error("Failed to download session logs", error as Error);
    } finally {
      closeMenu();
    }
  };

  const handleDownloadFiltered = async (filterTerm: string) => {
    try {
      const result = await downloadFilteredSessionLogBuffer(filterTerm);
      if (result.status === "missing") {
        logger.warn("No logs found in local storage session buffer.");
        return;
      }

      if (result.status === "noMatches") {
        logger.warn(`No logs found matching "${filterTerm}".`);
      }
    } catch (error) {
      logger.error("Failed to download filtered session logs", error as Error);
    }
  };

  const handleOpenDownloadFilteredDialog = () => {
    closeMenu();
    setFilterDialogOpen(true);
  };

  const handleConfirmDownloadFiltered = async () => {
    const normalizedFilterTerm = filterTermInput.trim();
    if (!normalizedFilterTerm) {
      closeFilterDialog();
      return;
    }

    await handleDownloadFiltered(normalizedFilterTerm);
    closeFilterDialog();
  };

  const handleClear = () => {
    try {
      clearSessionLogBuffer();
      logger.info("Session logs cleared.");
    } catch (error) {
      logger.error("Failed to clear session logs", error as Error);
    }
    closeMenu();
  };

  return (
    <>
      <div className="vnext-dev-log-download">
        {/* The visible text is the name (WCAG 2.5.3 Label in Name). */}
        <VNextButton
          onClick={() => {
            void handleDownload();
          }}
          tone="secondary"
        >
          Download logs
        </VNextButton>
        <div className="vnext-dev-log-download__menu-shell">
          <VNextButton
            aria-expanded={isMenuOpen}
            aria-haspopup="menu"
            aria-label="More log options"
            onClick={() => setMenuOpen((current) => !current)}
            tone="ghost"
          >
            More
          </VNextButton>
          {isMenuOpen ? (
            <VNextCard className="vnext-dev-log-download__menu">
              <div role="menu">
                <button
                  className="vnext-dev-log-download__menu-item"
                  onClick={handleOpenDownloadFilteredDialog}
                  role="menuitem"
                  type="button"
                >
                  Download with Filter...
                </button>
                <button
                  className="vnext-dev-log-download__menu-item"
                  onClick={handleClear}
                  role="menuitem"
                  type="button"
                >
                  Clear buffer
                </button>
              </div>
            </VNextCard>
          ) : null}
        </div>
      </div>

      <VNextDialog
        description="Use a case-insensitive message fragment to download only matching localhost session logs."
        footer={
          <>
            <VNextButton onClick={closeFilterDialog} tone="ghost">
              Cancel
            </VNextButton>
            <VNextButton
              onClick={() => {
                void handleConfirmDownloadFiltered();
              }}
              tone="primary"
            >
              Download
            </VNextButton>
          </>
        }
        onClose={closeFilterDialog}
        open={isFilterDialogOpen}
        title="Download filtered logs"
      >
        <div className="vnext-stack-sm">
          <VNextNotice tone="info">
            This helper is only shown on localhost and reads from the browser
            session log buffer.
          </VNextNotice>
          <VNextField
            hint="The message filter is case insensitive."
            label="Filter text (case insensitive)"
          >
            <VNextInput
              aria-label="Filter text (case insensitive)"
              autoFocus
              onChange={(event) => setFilterTermInput(event.target.value)}
              value={filterTermInput}
            />
          </VNextField>
        </div>
      </VNextDialog>
    </>
  );
};
